import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppConfig } from '@/hooks/useAppConfig';
import {
  offlineRecordsChosen,
  offlineToggleVisible,
  setOfflineRecordsChosen,
  subscribeOfflineRecords,
} from '@/lib/offlineRecords';

/** Якорь тумблера: на него ведёт подсказка у задания («Плохая связь? Включите …»). */
export const OFFLINE_RECORDS_ANCHOR = 'offline-records';

/**
 * Тумблер «Запись без связи» для экрана настроек (решение владельца 02.10.2026).
 *
 * `visible` — показывать ли его вообще: только приложение, серверный выключатель включён, плагин хранения
 * стоит, и на этом устройстве ученику уже встречалось задание с записью (`lib/offlineRecords.ts`). Выбор
 * живёт на устройстве, по умолчанию — выключено.
 */
export function useOfflineRecords(): { visible: boolean; on: boolean; toggle: () => void } {
  // Серверный выключатель приезжает запросом, после первой отрисовки, — без подписки строка осталась бы
  // такой, какой нарисовалась на умолчаниях.
  useAppConfig();
  const on = useSyncExternalStore(subscribeOfflineRecords, offlineRecordsChosen, offlineRecordsChosen);
  return { visible: offlineToggleVisible(), on, toggle: () => setOfflineRecordsChosen(!on) };
}

/** Пришли по ссылке с якорем — довести до самого тумблера, а не оставить наверху длинного экрана. */
export function useScrollToOfflineRecords(ready: boolean): void {
  const { hash } = useLocation();
  useEffect(() => {
    if (!ready || hash !== `#${OFFLINE_RECORDS_ANCHOR}`) return;
    document.getElementById(OFFLINE_RECORDS_ANCHOR)?.scrollIntoView({ block: 'center' });
  }, [ready, hash]);
}
